import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// ============================================================================
// ENUMS
// ============================================================================

export enum FolderDocumentRequestStatus {
  Created = 1,
  Sent = 2,
  PartiallyReceived = 3,
  Completed = 4,
  Expired = 5
}

// ============================================================================
// DTOs - Folders
// ============================================================================

export interface DocumentFolderDto {
  id: number;
  clientId: number;
  name: string;
  sortOrder: number;
  documentCount: number;
  newDocumentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDocumentFolderDto {
  clientId: number;
  name: string;
}

export interface UpdateDocumentFolderDto {
  name?: string;
  sortOrder?: number;
}

// ============================================================================
// DTOs - Documents
// ============================================================================

export interface FolderDocumentDto {
  id: number;
  clientId: number;
  folderId: number;
  folderName: string;
  originalFileName: string;
  fileExtension: string;
  fileSizeBytes: number;
  formattedFileSize: string;
  notes: string;
  createdAt: string;
  isNew: boolean;
}

export interface FolderDocumentUploadDto {
  clientId: number;
  folderId: number;
  file: File;
  notes?: string;
  fulfilledRequestItemId?: number;
}

// ============================================================================
// DTOs - Image Conversion
// ============================================================================

export interface ImageConversionResultDto {
  success: boolean;
  documentId: number;
  newFileName: string;
  newFileExtension: string;
  newFileSizeBytes: number;
  errorMessage?: string;
}

// ============================================================================
// DTOs - Requests
// ============================================================================

export interface FolderDocumentRequestDto {
  id: number;
  clientId: number;
  requestTitle: string;
  generalInstructions: string;
  publicId: string;
  hasAccessCode: boolean;
  accessCodeLastFour?: string;
  expiresAt: string;
  sentAt?: string;
  status: FolderDocumentRequestStatus;
  statusText: string;
  requestItems: FolderDocumentRequestItemDto[];
  receivedCount: number;
  totalCount: number;
  mandatoryCount: number;
  mandatoryReceivedCount: number;
  createdAt: string;
}

export interface FolderDocumentRequestItemDto {
  id: number;
  targetFolderId: number;
  targetFolderName: string;
  specialInstructions: string;
  isMandatory: boolean;
  isReceived: boolean;
  receivedAt?: string;
  // Uploaded document info (for preview in public upload page)
  uploadedDocumentId?: number;
  uploadedFileName?: string;
  uploadedFileExtension?: string;
}

export interface CreateFolderDocumentRequestDto {
  clientId: number;
  requestTitle: string;
  generalInstructions: string;
  accessCode?: string;
  items: CreateFolderDocumentRequestItemDto[];
}

export interface CreateFolderDocumentRequestItemDto {
  targetFolderId: number;
  specialInstructions: string;
  isMandatory: boolean;
}

export interface EditFolderDocumentRequestDto {
  requestTitle?: string;
  generalInstructions?: string;
  accessCode?: string;
  items?: EditFolderDocumentRequestItemDto[];
}

export interface EditFolderDocumentRequestItemDto {
  id?: number;
  targetFolderId: number;
  specialInstructions: string;
  isMandatory: boolean;
}

// ============================================================================
// DTOs - Public Upload
// ============================================================================

export interface FolderUploadSessionDto {
  requestId: number;
  clientId: number;
  clientName: string;
  requestTitle: string;
  generalInstructions: string;
  expiresAt: string;
  requestItems: FolderDocumentRequestItemDto[];
  totalCount: number;
  receivedCount: number;
  mandatoryCount: number;
  mandatoryReceivedCount: number;
}

export interface FolderAccessCodeVerificationResult {
  success: boolean;
  accessToken?: string;
  errorMessage?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getStatusText = (status: FolderDocumentRequestStatus): string => {
  switch (status) {
    case FolderDocumentRequestStatus.Created:
      return 'נוצר';
    case FolderDocumentRequestStatus.Sent:
      return 'נשלח';
    case FolderDocumentRequestStatus.PartiallyReceived:
      return 'התקבל חלקית';
    case FolderDocumentRequestStatus.Completed:
      return 'הושלם';
    case FolderDocumentRequestStatus.Expired:
      return 'פג תוקף';
    default:
      return 'לא ידוע';
  }
};

export const getStatusColor = (status: FolderDocumentRequestStatus): string => {
  switch (status) {
    case FolderDocumentRequestStatus.Created:
      return '#6c757d'; // Gray
    case FolderDocumentRequestStatus.Sent:
      return '#0073ea'; // Blue
    case FolderDocumentRequestStatus.PartiallyReceived:
      return '#d97706'; // Orange
    case FolderDocumentRequestStatus.Completed:
      return '#16a34a'; // Green
    case FolderDocumentRequestStatus.Expired:
      return '#dc2626'; // Red
    default:
      return '#6c757d';
  }
};

// ============================================================================
// API SERVICE - Folders
// ============================================================================

const folderDocumentsApi = {
  // Folders
  getFolders: async (clientId: number): Promise<DocumentFolderDto[]> => {
    const response = await axios.get<DocumentFolderDto[]>(
      `${API_BASE_URL}/folderdocuments/folders/client/${clientId}`
    );
    return response.data;
  },

  getFolderById: async (id: number): Promise<DocumentFolderDto> => {
    const response = await axios.get<DocumentFolderDto>(
      `${API_BASE_URL}/folderdocuments/folders/${id}`
    );
    return response.data;
  },

  createFolder: async (dto: CreateDocumentFolderDto): Promise<DocumentFolderDto> => {
    const response = await axios.post<DocumentFolderDto>(
      `${API_BASE_URL}/folderdocuments/folders`,
      dto
    );
    return response.data;
  },

  updateFolder: async (id: number, dto: UpdateDocumentFolderDto): Promise<DocumentFolderDto> => {
    const response = await axios.put<DocumentFolderDto>(
      `${API_BASE_URL}/folderdocuments/folders/${id}`,
      dto
    );
    return response.data;
  },

  deleteFolder: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/folderdocuments/folders/${id}`);
  },

  // Documents
  getDocumentsByFolder: async (folderId: number): Promise<FolderDocumentDto[]> => {
    const response = await axios.get<FolderDocumentDto[]>(
      `${API_BASE_URL}/folderdocuments/documents/folder/${folderId}`
    );
    return response.data;
  },

  getDocumentsByClient: async (clientId: number): Promise<FolderDocumentDto[]> => {
    const response = await axios.get<FolderDocumentDto[]>(
      `${API_BASE_URL}/folderdocuments/documents/client/${clientId}`
    );
    return response.data;
  },

  getDocumentById: async (id: number): Promise<FolderDocumentDto> => {
    const response = await axios.get<FolderDocumentDto>(
      `${API_BASE_URL}/folderdocuments/documents/${id}`
    );
    return response.data;
  },

  uploadDocument: async (dto: FolderDocumentUploadDto): Promise<FolderDocumentDto> => {
    const formData = new FormData();
    formData.append('file', dto.file);
    formData.append('ClientId', dto.clientId.toString());
    formData.append('FolderId', dto.folderId.toString());
    if (dto.notes) {
      formData.append('Notes', dto.notes);
    }
    if (dto.fulfilledRequestItemId) {
      formData.append('FulfilledRequestItemId', dto.fulfilledRequestItemId.toString());
    }

    const response = await axios.post<FolderDocumentDto>(
      `${API_BASE_URL}/folderdocuments/documents/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  downloadDocument: async (id: number): Promise<void> => {
    // Fetch the signed download URL with authentication, then open it
    try {
      const response = await axios.get<{ url: string; fileName: string }>(
        `${API_BASE_URL}/folderdocuments/documents/${id}/download-url`
      );
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  },

  getDownloadUrl: async (id: number): Promise<string> => {
    // Returns the signed B2 URL for downloading
    const response = await axios.get<{ url: string; fileName: string }>(
      `${API_BASE_URL}/folderdocuments/documents/${id}/download-url`
    );
    return response.data.url;
  },

  deleteDocument: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/folderdocuments/documents/${id}`);
  },

  convertToPdf: async (id: number): Promise<ImageConversionResultDto> => {
    const response = await axios.post<ImageConversionResultDto>(
      `${API_BASE_URL}/folderdocuments/documents/${id}/convert-to-pdf`
    );
    return response.data;
  },

  // Requests
  getRequestsByClient: async (clientId: number): Promise<FolderDocumentRequestDto[]> => {
    const response = await axios.get<FolderDocumentRequestDto[]>(
      `${API_BASE_URL}/folderdocuments/requests/client/${clientId}`
    );
    return response.data;
  },

  getRequestById: async (id: number): Promise<FolderDocumentRequestDto> => {
    const response = await axios.get<FolderDocumentRequestDto>(
      `${API_BASE_URL}/folderdocuments/requests/${id}`
    );
    return response.data;
  },

  createRequest: async (dto: CreateFolderDocumentRequestDto): Promise<FolderDocumentRequestDto> => {
    const response = await axios.post<FolderDocumentRequestDto>(
      `${API_BASE_URL}/folderdocuments/requests`,
      dto
    );
    return response.data;
  },

  editRequest: async (id: number, dto: EditFolderDocumentRequestDto): Promise<FolderDocumentRequestDto> => {
    const response = await axios.put<FolderDocumentRequestDto>(
      `${API_BASE_URL}/folderdocuments/requests/${id}`,
      dto
    );
    return response.data;
  },

  sendRequest: async (id: number): Promise<FolderDocumentRequestDto> => {
    const response = await axios.post<FolderDocumentRequestDto>(
      `${API_BASE_URL}/folderdocuments/requests/${id}/send`
    );
    return response.data;
  },

  resendRequest: async (id: number): Promise<FolderDocumentRequestDto> => {
    const response = await axios.post<FolderDocumentRequestDto>(
      `${API_BASE_URL}/folderdocuments/requests/${id}/resend`
    );
    return response.data;
  },

  cancelRequest: async (id: number): Promise<void> => {
    await axios.post(`${API_BASE_URL}/folderdocuments/requests/${id}/cancel`);
  },
};

// ============================================================================
// PUBLIC UPLOAD API (for clients - no auth required)
// ============================================================================

export const publicFolderUploadApi = {
  verifyAccessCode: async (publicId: string, accessCode: string): Promise<{ token: string; message: string }> => {
    const response = await axios.post<{ token: string; message: string }>(
      `${API_BASE_URL}/public/folder-upload/verify-access/${publicId}`,
      { accessCode }
    );
    return response.data;
  },

  getUploadSession: async (publicId: string, accessToken: string): Promise<FolderUploadSessionDto> => {
    const response = await axios.get<FolderUploadSessionDto>(
      `${API_BASE_URL}/public/folder-upload/session/${publicId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  },

  uploadDocument: async (
    publicId: string,
    accessToken: string,
    file: File,
    clientId: number,
    folderId: number,
    notes?: string,
    fulfilledRequestItemId?: number
  ): Promise<{ success: boolean; documentId: number; fileName: string; folderName: string; size: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('ClientId', clientId.toString());
    formData.append('FolderId', folderId.toString());
    if (notes) {
      formData.append('Notes', notes);
    }
    if (fulfilledRequestItemId) {
      formData.append('FulfilledRequestItemId', fulfilledRequestItemId.toString());
    }

    const response = await axios.post(
      `${API_BASE_URL}/public/folder-upload/${publicId}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  },

  uploadMultipleDocuments: async (
    publicId: string,
    accessToken: string,
    files: { file: File; clientId: number; folderId: number; notes?: string; fulfilledRequestItemId?: number }[]
  ): Promise<{
    uploadResults: { success: boolean; fileName: string; documentId: number; folderName: string; size: string }[];
    errors: string[];
    totalUploaded: number;
    totalErrors: number;
  }> => {
    const formData = new FormData();

    files.forEach((item, index) => {
      formData.append('files', item.file);
      formData.append('clientIds', item.clientId.toString());
      formData.append('folderIds', item.folderId.toString());
      formData.append('notes', item.notes || '');
      formData.append('fulfilledItemIds', item.fulfilledRequestItemId?.toString() || '0');
    });

    const response = await axios.post(
      `${API_BASE_URL}/public/folder-upload/${publicId}/upload-multiple`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  },

  getDocumentPreviewUrl: async (
    publicId: string,
    accessToken: string,
    documentId: number
  ): Promise<string> => {
    const response = await axios.get<{ url: string }>(
      `${API_BASE_URL}/public/folder-upload/${publicId}/document/${documentId}/preview-url`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data.url;
  },

  completeUploadSession: async (
    publicId: string,
    accessToken: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await axios.post<{ success: boolean; message: string }>(
      `${API_BASE_URL}/public/folder-upload/${publicId}/complete`,
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  },
};

export default folderDocumentsApi;
